import { useState } from 'react';
import { createPortal } from 'react-dom';
import { registerPlugin } from '@capacitor/core';
import type { ApiClient } from '../api';
import type { Me } from '../types';
import { scriptableWidget } from '../widgetScript';
import { inApp } from './PhoneAlerts';

/**
 * The A.C.C.R.A iPhone app's own widget (accra-app/ios/App/ACCRAWidget,
 * 1 November 2026). Inside the app, "📲 Widget" hands the read-only key
 * straight to the native widget (AccraWidgetPlugin.swift, which keeps it
 * in the iPhone Keychain for the widget alone), so there is nothing to
 * copy or paste. The key is never shown on screen here.
 */
interface AccraWidgetPlugin {
  connect(o: { token: string }): Promise<{ connected: boolean }>;
}
const AccraWidget = registerPlugin<AccraWidgetPlugin>('AccraWidget');

/**
 * "📲 Widget" in the header (owners only): sets an iPhone up with the
 * A.C.C.R.A home-screen widget through Scriptable, a free iOS app that
 * runs a script as a widget. The script comes ready to paste, with this
 * dashboard's address and the widget's read-only key filled in
 * (shell/api/src/widget.ts). The key is fetched only when this opens and
 * is never kept anywhere in the browser.
 */
export function WidgetSetup({ api, me }: { api: ApiClient; me: Me }) {
  const [open, setOpen] = useState(false);
  const [script, setScript] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  if (me.role !== 'owner') return null;

  const show = async () => {
    setOpen(true);
    setNote(null);
    if (inApp()) {
      try {
        const { token } = await api.get<{ token: string }>('/api/widget-setup');
        await AccraWidget.connect({ token });
        setNote('✅ Widget connected. Now add it to your Home Screen or Lock Screen (steps above).');
      } catch (err) {
        const msg = String((err as Error).message);
        setNote(
          /501/.test(msg)
            ? 'The widget is not configured on the server yet.'
            : /not implemented/i.test(msg)
              ? 'Update A.C.C.R.A from TestFlight first, then tap 📲 Widget again.'
              : 'Could not connect the widget. Try again.',
        );
      }
      return;
    }
    try {
      const { token } = await api.get<{ token: string }>('/api/widget-setup');
      setScript(scriptableWidget(window.location.origin, token));
    } catch (err) {
      setNote(/501/.test(String((err as Error).message)) ? 'The widget is not configured on the server yet.' : 'Could not load the widget key.');
    }
  };

  const copy = async () => {
    if (!script) return;
    try {
      await navigator.clipboard.writeText(script);
      setNote('Copied. Now paste it into a new Scriptable script.');
    } catch {
      setNote('Copy was blocked: select the text below and copy it by hand.');
    }
  };

  const close = () => {
    setOpen(false);
    setScript(null); // the key leaves memory with the panel
  };

  return (
    <>
      <button type="button" className="cc-widget-btn" onClick={show} title="Put A.C.C.R.A on your iPhone home screen">
        📲<span> Widget</span>
      </button>
      {/* Portalled to <body>: the topbar's backdrop-filter makes it the
          containing block for fixed children, which trapped the sheet
          inside the header bar. */}
      {open && createPortal(
        <div className="cc-sheet-backdrop" onClick={close}>
          <section className="cc-sheet" role="dialog" aria-label="iPhone widget" onClick={(e) => e.stopPropagation()}>
            <header>
              <h3>📲 iPhone widget</h3>
              <button type="button" onClick={close}>
                Close
              </button>
            </header>
            {inApp() ? (
              <ol>
                <li>Leave this open a second while it connects (you'll see ✅ below).</li>
                <li>
                  Home Screen: press and hold an empty spot, tap <strong>Edit</strong> then <strong>Add Widget</strong>,
                  search <strong>A.C.C.R.A</strong>, pick small, medium or large (large shows everything), then{' '}
                  <strong>Add Widget</strong>.
                </li>
                <li>
                  Lock Screen (optional): press and hold it, <strong>Customise</strong>, <strong>Lock Screen</strong>,
                  tap the widget area and choose <strong>A.C.C.R.A</strong>.
                </li>
              </ol>
            ) : (
            <ol>
              <li>
                Install <strong>Scriptable</strong> (free) from the App Store.
              </li>
              <li>
                Tap <strong>Copy script</strong> below. In Scriptable tap <strong>+</strong>, paste, and name it{' '}
                <strong>Accra</strong>.
              </li>
              <li>
                Home screen: press and hold, tap <strong>+</strong>, choose <strong>Scriptable</strong> and a size, add
                it, then tap it and set <strong>Script</strong> to <strong>Accra</strong>.
              </li>
              <li>
                Lock screen (optional): press and hold it, <strong>Customise</strong>, add a Scriptable widget and pick{' '}
                <strong>Accra</strong>.
              </li>
            </ol>
            )}
            <p className="cc-sheet-note">
              It shows the ticker's running totals and the latest event types, never names. iOS refreshes widgets every
              15 to 60 minutes; alerts stay the instant channel. The script holds a private read-only key: don't share
              it.
            </p>
            {!inApp() && (
              <button type="button" className="cc-sheet-primary" onClick={copy} disabled={!script}>
                Copy script
              </button>
            )}
            {note && <p className="cc-sheet-note">{note}</p>}
            {script && <textarea className="cc-sheet-code" readOnly value={script} rows={6} aria-label="Widget script" />}
          </section>
        </div>,
        document.body,
      )}
    </>
  );
}
